import type { MouseEvent } from "react";
import { useState } from "react";
import type { User } from "../api";
import { api } from "../api";

type Props = {
  token: string;
  user: User;
  onClose: () => void;
  onDeleted?: () => void;
};

export function DeleteUserModal({ token, user, onClose, onDeleted }: Props) {
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onBackdrop = (ev: MouseEvent<HTMLDivElement>) => {
    if (ev.target === ev.currentTarget && !busy) onClose();
  };

  const matches = confirmText.trim() === user.username;

  const onConfirm = async () => {
    if (!matches) return;
    setError(null);
    setBusy(true);
    try {
      await api.deleteUser(token, user.id);
      onDeleted?.();
      onClose();
    } catch (e: unknown) {
      const msg =
        typeof e === "object" && e && "message" in e ? String((e as { message?: string }).message) : String(e);
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onMouseDown={onBackdrop}
      role="presentation"
    >
      <div
        className="bg-white rounded-lg border border-slate-200 shadow-xl max-w-md w-full flex flex-col"
        role="dialog"
        aria-labelledby="delete-user-modal-title"
        onMouseDown={(ev) => ev.stopPropagation()}
      >
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h3 id="delete-user-modal-title" className="text-base font-semibold text-red-700">
              Delete user
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              <span className="font-mono">{user.username}</span> (id {user.id})
              {user.is_admin && (
                <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide">
                  ★ admin
                </span>
              )}
            </p>
          </div>
          <button type="button" onClick={onClose} className="btn shrink-0" disabled={busy} aria-label="Close">
            Close
          </button>
        </div>

        <div className="p-4 space-y-3">
          {error && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</div>
          )}

          <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            <p className="font-medium">This is permanent.</p>
            <ul className="list-disc pl-5 mt-1 space-y-0.5 text-xs">
              <li>All <code>schema_grants</code> assigned to this user are deleted.</li>
              <li>The user's saved Bearer JWTs continue to fail (401) until they expire.</li>
              <li>Database tables they may have created via Claude are untouched (the MCP is read-only).</li>
            </ul>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">
              Type <span className="font-mono text-slate-900">{user.username}</span> to confirm
            </label>
            <input
              type="text"
              className="input"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder={user.username}
              autoFocus
            />
          </div>
        </div>

        <div className="px-4 py-3 border-t border-slate-100 flex justify-end gap-2 bg-slate-50 rounded-b-lg">
          <button type="button" className="btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-danger"
            disabled={busy || !matches}
            onClick={() => void onConfirm()}
            title={matches ? "Permanently delete user" : "Type the username to enable"}
          >
            {busy ? "Deleting…" : "Delete user"}
          </button>
        </div>
      </div>
    </div>
  );
}
